import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Building2, User, CreditCard, Calendar, Eye, EyeOff } from 'lucide-react';

function CreateBusinessPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [showCredentials, setShowCredentials] = useState(false);
    const [createdBusiness, setCreatedBusiness] = useState(null);

    const { register, handleSubmit, watch, formState: { errors }, setValue } = useForm({
        defaultValues: {
            businessName: '',
            subdomain: '',
            ownerEmail: '',
            ownerFirstName: '',
            ownerLastName: '',
            ownerUsername: '',
            ownerPassword: '',
            planId: '',
            trialDays: 14
        }
    });

    // Watch subdomain to auto-generate from business name
    const businessName = watch('businessName');
    const subdomain = watch('subdomain');

    // Auto-generate subdomain from business name
    React.useEffect(() => {
        if (businessName && !subdomain) {
            const generatedSubdomain = businessName
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '-')
                .substring(0, 30);
            setValue('subdomain', generatedSubdomain);
        }
    }, [businessName, subdomain, setValue]);

    // Fetch available plans
    const { data: plans, isLoading: isLoadingPlans } = useQuery({
        queryKey: ['plans'],
        queryFn: async () => {
            const response = await api.get('/admin/plans');
            return response.data.data; // Backend returns { success, data: [...] }
        }
    });

    // Create business mutation
    const createBusinessMutation = useMutation({
        mutationFn: async (businessData) => {
            const response = await api.post('/admin/businesses', businessData);
            return response.data.data; // Backend returns { success, data }
        },
        onSuccess: (data) => {
            setCreatedBusiness(data);
            setShowCredentials(true);
            toast.success('Business created successfully!');
            queryClient.invalidateQueries(['businesses']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create business');
        }
    });

    const onSubmit = async (data) => {
        createBusinessMutation.mutate(data);
    };

    const handleBack = () => {
        navigate('/businesses');
    };

    const handleCreateAnother = () => {
        setCreatedBusiness(null);
        setShowCredentials(false);
        // Reset form
        setValue('businessName', '');
        setValue('subdomain', '');
        setValue('ownerEmail', '');
        setValue('ownerFirstName', '');
        setValue('ownerLastName', '');
        setValue('ownerUsername', '');
        setValue('ownerPassword', '');
        setValue('planId', '');
        setValue('trialDays', 14);
    };

    if (isLoadingPlans) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading plans...</p>
                </div>
            </div>
        );
    }

    if (showCredentials && createdBusiness) {
        return (
            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                        <div className="bg-green-600 px-6 py-4">
                            <h1 className="text-2xl font-bold text-white flex items-center">
                                <Building2 className="mr-3" size={28} />
                                Business Created Successfully!
                            </h1>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Business Information */}
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                                        <Building2 className="mr-2" size={20} />
                                        Business Details
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">Name:</span> {createdBusiness.business.name}</p>
                                        <p><span className="font-medium">Subdomain:</span> {createdBusiness.business.subdomain}.easica.com</p>
                                        <p><span className="font-medium">Plan:</span> {createdBusiness.business.plan}</p>
                                        <p><span className="font-medium">Status:</span> 
                                            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                                                createdBusiness.business.status === 'active' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {createdBusiness.business.status}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Owner Information */}
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                                        <User className="mr-2" size={20} />
                                        Owner Account
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">Name:</span> {createdBusiness.owner.firstName} {createdBusiness.owner.lastName}</p>
                                        <p><span className="font-medium">Email:</span> {createdBusiness.owner.email}</p>
                                        <p><span className="font-medium">Role:</span> Owner</p>
                                    </div>
                                </div>
                            </div>

                            {/* Credentials */}
                            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center">
                                    <CreditCard className="mr-2" size={20} />
                                    Owner Login Credentials
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Login URL
                                        </label>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={createdBusiness.loginUrl}
                                                readOnly
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                                            />
                                            <button
                                                onClick={() => navigator.clipboard.writeText(createdBusiness.loginUrl)}
                                                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Username
                                            </label>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    value={createdBusiness.ownerUsername}
                                                    readOnly
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                                                />
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(createdBusiness.ownerUsername)}
                                                    className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Email
                                            </label>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    value={createdBusiness.owner.email}
                                                    readOnly
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                                                />
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(createdBusiness.owner.email)}
                                                    className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-xs font-semibold text-blue-900 mb-2">📝 Important Setup Instructions:</p>
                                    <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                                        <li>Access the login page using the subdomain URL above</li>
                                        <li>Or set <code className="bg-blue-100 px-1 rounded">VITE_TENANT_ID={createdBusiness.business.subdomain}</code> in scheduling-app/.env</li>
                                        <li>Login with the username and password provided</li>
                                    </ol>
                                </div>
                            </div>

                            {/* Trial Information */}
                            {createdBusiness.subscription && (
                                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center">
                                        <Calendar className="mr-2" size={20} />
                                        Trial Period
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium">Trial Start:</span>
                                            <p className="text-gray-600">{new Date(createdBusiness.subscription.trialStart).toLocaleDateString()}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium">Trial End:</span>
                                            <p className="text-gray-600">{new Date(createdBusiness.subscription.trialEnd).toLocaleDateString()}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium">Duration:</span>
                                            <p className="text-gray-600">{createdBusiness.subscription.trialDays} days</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="mt-8 flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => navigate(`/businesses/${createdBusiness.business.id}`)}
                                    className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                >
                                    View Business Details
                                </button>
                                <button
                                    onClick={handleCreateAnother}
                                    className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                >
                                    Create Another Business
                                </button>
                                <button
                                    onClick={handleBack}
                                    className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                                >
                                    Back to Businesses
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={handleBack}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Back to Businesses
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900">Create New Business</h1>
                    <p className="mt-2 text-gray-600">
                        Set up a new driving school with owner account and trial subscription.
                    </p>
                </div>

                {/* Form */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <form onSubmit={handleSubmit(onSubmit)} className="p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Left Column - Business Information */}
                            <div className="space-y-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                        <Building2 className="mr-2" size={24} />
                                        Business Information
                                    </h2>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                                                Business Name *
                                            </label>
                                            <input
                                                type="text"
                                                id="businessName"
                                                {...register('businessName', { 
                                                    required: 'Business name is required',
                                                    minLength: { value: 2, message: 'Business name must be at least 2 characters' }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="ABC Driving School"
                                            />
                                            {errors.businessName && (
                                                <p className="text-red-500 text-sm mt-1">{errors.businessName.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-1">
                                                Subdomain (Optional)
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="text"
                                                    id="subdomain"
                                                    {...register('subdomain', { 
                                                        pattern: {
                                                            value: /^[a-z0-9-]+$/,
                                                            message: 'Subdomain can only contain lowercase letters, numbers, and hyphens'
                                                        },
                                                        minLength: { value: 3, message: 'Subdomain must be at least 3 characters' },
                                                        maxLength: { value: 30, message: 'Subdomain must be less than 30 characters' }
                                                    })}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="abc-driving-school (auto-generated if empty)"
                                                />
                                                <span className="ml-2 text-gray-500 text-sm">.easica.com</span>
                                            </div>
                                            {errors.subdomain && (
                                                <p className="text-red-500 text-sm mt-1">{errors.subdomain.message}</p>
                                            )}
                                            <p className="text-xs text-gray-500 mt-1">
                                                Leave empty to auto-generate from business name: {subdomain || 'will-be-auto-generated'}.easica.com
                                            </p>
                                        </div>

                                        <div>
                                            <label htmlFor="planId" className="block text-sm font-medium text-gray-700 mb-1">
                                                Subscription Plan *
                                            </label>
                                            <select
                                                id="planId"
                                                {...register('planId', { required: 'Plan selection is required' })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="">Select a plan</option>
                                                {plans?.map(plan => (
                                                    <option key={plan.id} value={plan.id}>
                                                        {plan.name} - ${plan.price}/month
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.planId && (
                                                <p className="text-red-500 text-sm mt-1">{errors.planId.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="trialDays" className="block text-sm font-medium text-gray-700 mb-1">
                                                Trial Days
                                            </label>
                                            <input
                                                type="number"
                                                id="trialDays"
                                                {...register('trialDays', { 
                                                    required: 'Trial days is required',
                                                    min: { value: 1, message: 'Trial must be at least 1 day' },
                                                    max: { value: 90, message: 'Trial cannot exceed 90 days' }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                min="1"
                                                max="90"
                                            />
                                            {errors.trialDays && (
                                                <p className="text-red-500 text-sm mt-1">{errors.trialDays.message}</p>
                                            )}
                                            <p className="text-xs text-gray-500 mt-1">
                                                Number of days for the trial period (1-90 days)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Owner Information */}
                            <div className="space-y-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                        <User className="mr-2" size={24} />
                                        Owner Account
                                    </h2>
                                    
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="ownerFirstName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    First Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="ownerFirstName"
                                                    {...register('ownerFirstName', { 
                                                        required: 'First name is required',
                                                        minLength: { value: 2, message: 'First name must be at least 2 characters' }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="John"
                                                />
                                                {errors.ownerFirstName && (
                                                    <p className="text-red-500 text-sm mt-1">{errors.ownerFirstName.message}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="ownerLastName" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Last Name (Optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    id="ownerLastName"
                                                    {...register('ownerLastName', { 
                                                        minLength: { value: 2, message: 'Last name must be at least 2 characters' }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Doe (optional)"
                                                />
                                                {errors.ownerLastName && (
                                                    <p className="text-red-500 text-sm mt-1">{errors.ownerLastName.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="ownerEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                                Email Address (Optional)
                                            </label>
                                            <input
                                                type="email"
                                                id="ownerEmail"
                                                {...register('ownerEmail', { 
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: 'Please enter a valid email address'
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="owner@drivingschool.com (optional)"
                                            />
                                            {errors.ownerEmail && (
                                                <p className="text-red-500 text-sm mt-1">{errors.ownerEmail.message}</p>
                                            )}
                                            <p className="text-xs text-gray-500 mt-1">
                                                Email is optional. Users can login with username only.
                                            </p>
                                        </div>

                                        {/* Login Credentials Section */}
                                        <div className="border-t border-gray-200 pt-4 mt-4">
                                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Login Credentials</h3>
                                            
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="ownerUsername" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Username *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="ownerUsername"
                                                        {...register('ownerUsername', { 
                                                            required: 'Username is required',
                                                            minLength: { value: 3, message: 'Username must be at least 3 characters' },
                                                            pattern: {
                                                                value: /^[a-zA-Z0-9_]+$/,
                                                                message: 'Username can only contain letters, numbers, and underscores'
                                                            }
                                                        })}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="owner_username"
                                                    />
                                                    {errors.ownerUsername && (
                                                        <p className="text-red-500 text-sm mt-1">{errors.ownerUsername.message}</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Only letters, numbers, and underscores allowed
                                                    </p>
                                                </div>

                                                <div>
                                                    <label htmlFor="ownerPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Password *
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type={showPassword ? 'text' : 'password'}
                                                            id="ownerPassword"
                                                            {...register('ownerPassword', { 
                                                                required: 'Password is required',
                                                                minLength: { value: 8, message: 'Password must be at least 8 characters' }
                                                            })}
                                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            placeholder="Enter secure password"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                                        >
                                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                        </button>
                                                    </div>
                                                    {errors.ownerPassword && (
                                                        <p className="text-red-500 text-sm mt-1">{errors.ownerPassword.message}</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Minimum 8 characters required
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createBusinessMutation.isLoading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {createBusinessMutation.isLoading ? (
                                        <span className="flex items-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Creating Business...
                                        </span>
                                    ) : (
                                        'Create Business'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CreateBusinessPage;
